import React, {useEffect} from 'react';
import MDSpinner from 'react-md-spinner';
import ShowtimesBlockCinema from './ShowtimesBlockCinema';
import { getShowtimeDayTimeLimits, isShowtimeAvailableForDate } from '../../../utils/showtimes';
import { extractTimeZoneFromIsoDate } from '../../../utils/dates';
import { ShowtimesBlockProps } from './ShowtimesBlock';

const filterVersionsByLangAndFormat = (versions: any[], lang: string, formatFilters: Array<string>) => {
    return versions.filter(version =>
        version.type.includes(lang) &&
        (!formatFilters.length || formatFilters.every(type => version.type.includes(type)))
    );
};

const getSessionsForDate = (version: any, dateLimits: { min: number, max: number }) => {
    return version.sessions.filter(session => isShowtimeAvailableForDate(new Date(session.datetime).getTime() / 1000, dateLimits));
};

const buildShowtimesForCinema = (cinema: any, lang: string, filters: any, dateLimits: { min: number, max: number }) => {
    const sessions: Array<any> = [];
    const filteredVersions = filterVersionsByLangAndFormat(cinema.versions, lang, filters.format);

    filteredVersions.forEach(version => {
        const versionSessions = getSessionsForDate(version, dateLimits);
        versionSessions.forEach(session => {
            sessions.push({ version, session });
        });
    });

    return sessions;
};

const buildShowtimesForLanguage = (lang: string, cinemas: any[], filters: any, dateLimits: { min: number, max: number }) => {
    const langShowtimes: { [key: string]: Array<any> } = {};

    cinemas.forEach(cinema => {
        langShowtimes[cinema.cinema.id] = buildShowtimesForCinema(cinema, lang, filters, dateLimits);
        if (!langShowtimes[cinema.cinema.id].length) {
            delete langShowtimes[cinema.cinema.id];
        }
    });

    return langShowtimes;
};

const sortShowtimesByDatetime = (showtimes: any) => {
    Object.keys(showtimes).forEach(lang => {
        Object.keys(showtimes[lang]).forEach(cinemaId => {
            showtimes[lang][cinemaId].sort((a, b) => 
                a.session.datetime.localeCompare(b.session.datetime)
            );
        });
    });
};

const isDataEmpty = (data: any) => {
    const cinemas = data && data.cinemas;
    if (!Array.isArray(cinemas) || !cinemas.length) return true;

    const hasAtLeastOneSession = cinemas.some(cinema =>
        Array.isArray(cinema.versions) && cinema.versions.some(version =>
            Array.isArray(version.sessions) && version.sessions.length > 0
        )
    );

    return !hasAtLeastOneSession;
};

const getFirstSessionDatetime = (cinemas: any[]) => {
    for (const cinema of cinemas) {
        for (const version of cinema.versions) {
            if (version.sessions && version.sessions.length > 0) {
                return version.sessions[0].datetime;
            }
        }
    }
    return null;
};

const ShowtimesBlockContent = (props: ShowtimesBlockProps) => {
    const [showtimes, setShowtimes] = React.useState<{ [key: string]: { [key: string]: Array<any> } }>({});
    const [dateLimits, setDateLimits] = React.useState<{ min: number, max: number } | null>(null);

    useEffect(() => {
        if (!props.data || !props.date || isDataEmpty(props.data)) {
            setDateLimits(null);
            return;
        }

        setDateLimits(
            getShowtimeDayTimeLimits(
                props.date,
                extractTimeZoneFromIsoDate(getFirstSessionDatetime(props.data.cinemas))
            )
        );
    }, [props.data, props.date]);
    
    useEffect(() => {
        if (!props.data || !props.languages || !props.date || !dateLimits) {
            setShowtimes({});
            return;
        }

        const filteredLanguages = props.languages.filter(lang =>
            !props.filters.lang.length || props.filters.lang.includes(lang)
        );

        const showtimes: { [key: string]: { [key: string]: Array<any> } } = {};

        filteredLanguages.forEach(lang => {
            showtimes[lang] = buildShowtimesForLanguage(lang, props.data.cinemas, props.filters, dateLimits);
            if (!Object.keys(showtimes[lang]).length) {
                delete showtimes[lang];
            }
        });

        sortShowtimesByDatetime(showtimes);
        setShowtimes(showtimes);
    }, [props.data, props.languages, props.date, props.filters, dateLimits]);

    if (props.loading || !props.attributes || !props.movie) {
        return <div className="py-5 text-center"><MDSpinner singleColor="var(--primary)" /></div>;
    }

    if (!props.data || !Object.keys(showtimes).length) {
        return <div className="pm-3">{props.emptyMessage}</div>;
    }
    
    return <div>
        {props.data.cinemas.map((cinema, i) => <ShowtimesBlockCinema
            key={cinema.cinema.id}
            cinema={cinema}
            movie={props.movie}
            showtimes={showtimes}
            attributes={props.attributes}
            languages={props.languages}
            onCinemaFave={props.onCinemaFave}
            isFave={!!props.favCinemasIds && props.favCinemasIds.includes(cinema.cinema.id)}
            userLocation={props.userLocation}
        />)}
    </div>;
}

export default ShowtimesBlockContent;